import { useState, useEffect } from 'react';
import { useReactFlow } from 'reactflow';
import { useParams } from 'react-router-dom';
// Usando a versão do contexto para acessar o produto ativo
import { useWorkspace } from '@/features/Workspaces/state/WorkspaceContext';
import { NodeTestingComponent } from './NodeTestingComponent';
import { executeNodeWithPolling, updateWorkflowEdges } from '../utils/node.utils';
import { useToast } from '@/shared/components/ui/use-toast';
import StorageTriggerWaitingDialog from './StorageTriggerWaitingDialog';
import { useTranslation } from 'react-i18next';
import { resolveApiErrorMessage } from '@/shared/utils/apiErrorMessage';
export interface NodeTestingProps {
  node: Record<string, unknown> | null;
  formValues: Record<string, unknown>;
  isTesting: boolean;
  setIsTesting: (isTesting: boolean) => void;
  testOutput: unknown;
  setTestOutput: (testOutput: unknown) => void;
  testStatus: string | null;
  setTestStatus: (testStatus: string | null) => void;
  nodeOutput: unknown;
  setNodeOutput: (nodeOutput: unknown) => void;
}

export function NodeTesting({
  node,
  formValues,
  isTesting,
  setIsTesting,
  testOutput,
  setTestOutput,
  testStatus,
  setTestStatus,
  nodeOutput,
  setNodeOutput
}: NodeTestingProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { getEdges, setEdges } = useReactFlow();
  const params = useParams();
  const { currentWorkspace } = useWorkspace();
  
  const [input, setInput] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [isStorageTriggerWaiting, setIsStorageTriggerWaiting] = useState(false);
  const [currentStorageTriggerNodeId, setCurrentStorageTriggerNodeId] = useState('');
  const [organizationId, setOrganizationId] = useState<string>('');
  const [workspaceId, setProductId] = useState<string>('');
  const [fileTypes, setFileTypes] = useState<string[]>([]);

  // Extrai o workspaceId da URL
  const productIdFromParams = params.workspaceId as string || '';
  
  // Usa o workspaceId do contexto de produto se disponível
  const contextProductId = currentWorkspace?.id || '';

  // Reset do estado ao alterar o nó selecionado
  useEffect(() => {
    if (node) {
      setInput('');
      setError(null);
      setTestOutput(null);
      setTestStatus(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node?.id, setTestOutput, setTestStatus]);

  // Configurar organizationId e workspaceId ao carregar o componente
  useEffect(() => {
    // organizationId: prioridade para currentWorkspace (já reflete a org correta para superadmins)
    if (currentWorkspace?.organization_id) {
      setOrganizationId(currentWorkspace.organization_id);
    }

    // workspaceId: prioridade URL params > contexto do produto
    const effectiveProductId = productIdFromParams || contextProductId;
    if (effectiveProductId) {
      setProductId(effectiveProductId);
    }
  }, [productIdFromParams, contextProductId, currentWorkspace?.organization_id]);

  // Verificar se todos os parâmetros necessários estão disponíveis
  useEffect(() => {

    // Atualizar os estados com os parâmetros da URL
    if (params.organizationId) {
      setOrganizationId(params.organizationId);
    }
    
    if (productIdFromParams) {
      setProductId(productIdFromParams);
    }
  }, [params.organizationId, productIdFromParams]);

  // Atualiza as edges do workflow quando o node for testado com sucesso
  useEffect(() => {
    if (node && testOutput && !isTesting && testStatus === 'success') {
      updateWorkflowEdges(node as import('reactflow').Node, testOutput);
      
      // Utiliza o nodeOutput para manter a compatibilidade com o código existente
      if (nodeOutput !== testOutput) {
        setNodeOutput(testOutput);
      }
    }
  }, [node, testOutput, isTesting, testStatus, getEdges, setEdges, nodeOutput, setNodeOutput]);

  // Handler para teste de nó
  const handleTestNode = async () => {
    // Usar o produto ativo do contexto (já inicializado no nível superior do componente)
    // e o organizationId do estado (que pode ser do parâmetro de URL)
    const effectiveOrgId = currentWorkspace?.organization_id || organizationId;
    const effectiveProdId = currentWorkspace?.id || workspaceId;
    
    if (!node) {
      const errorMessage = t('Nó não selecionado para teste.');
      setError(errorMessage);
      toast({
        title: t('Erro ao testar nó'),
        description: errorMessage,
        variant: 'destructive',
      });
      return;
    }

    try {
      setIsTesting(true);
      setError(null);
      setTestOutput(null);
      setTestStatus(null);
      setNodeOutput(null);

      // Verificação especial para storage_trigger
      const nodeData = node.data as Record<string, unknown> | undefined;
      const nodeConfig = nodeData?.config as Record<string, unknown> | undefined;
      if (nodeData?.type === 'storage_trigger') {
        // Extrair fileTypes do formulário
        const configFileTypes = formValues.fileType
          ? [formValues.fileType as string]
          : ((formValues.fileTypes as string[]) || []);

        setFileTypes(configFileTypes);
        setIsStorageTriggerWaiting(true);
        setCurrentStorageTriggerNodeId(String(node.id));
        return;
      }

      // Preparar input: usar os dados de formValues ou o texto do input
      let parsedInput: unknown = formValues?.input || {};
      if (input.trim() && !formValues?.input) {
        try {
          parsedInput = JSON.parse(input);
        } catch (error) {
          // Se não for JSON válido e o formato esperado for JSON, usar o texto como está
          parsedInput = nodeConfig?.input_format === 'json'
            ? { text: input }  // Evita usar objeto vazio para corrigir erro de tipagem
            : { text: input };
        }
      }

      // Executar o nó com polling adequado para tipos assíncronos
      const result = await executeNodeWithPolling(
        node as import('reactflow').Node,
        parsedInput,
        effectiveOrgId, // Usar o organizationId efetivo (JWT ou fallback)
        effectiveProdId, // Usar o workspaceId efetivo (JWT ou fallback)
        formValues // Passar os valores do formulário para serem mesclados com a configuração
      );

      setTestOutput(result);
      setNodeOutput(result);
      setTestStatus('success');
      
      // Notificar o usuário
      toast({
        title: t('Teste concluído'),
        description: t('Nó testado com sucesso'),
        variant: 'default',
      });
    } catch (error: unknown) {
      const errorMsg = resolveApiErrorMessage(error, t);
      setError(errorMsg);
      setTestStatus('error');
      
      toast({
        title: t('Erro ao testar nó'),
        description: errorMsg,
        variant: 'destructive',
      });
      
      console.error('Erro ao testar nó:', error);
    } finally {
      setIsTesting(false);
    }
  };

  // Função para processar dados enviados pelo Storage Trigger
  const handleStorageData = (data: unknown) => {

    setTestOutput(data);
    setNodeOutput(data);
    setTestStatus('success');
    
    // Atualizar as edges do workflow
    if (node) {
      updateWorkflowEdges(node as import('reactflow').Node, data);
    }

    setIsStorageTriggerWaiting(false);
    
    toast({
      title: t('Teste concluído'),
      description: t('Storage trigger processado com sucesso'),
      variant: 'default',
    });
  };

  // Função para lidar com timeout do Storage Trigger
  const handleStorageTriggerTimeout = (_nodeId: string) => {

    setTestStatus('error');
    setTestOutput({
      error: t('workflow.storage_trigger.error.timeout', 'O tempo limite para o upload do arquivo foi excedido')
    });
    
    setIsTesting(false);
    setIsStorageTriggerWaiting(false);
    
    toast({
      title: t('workflow.storage_trigger.error.timeout_title', 'Tempo Esgotado'),
      description: t('workflow.storage_trigger.error.timeout_description', 'O tempo limite para o upload do arquivo foi excedido. Por favor, tente novamente.'),
      variant: 'destructive',
    });
  };

  // Se não houver nó selecionado, não renderizar nada
  if (!node) {
    return null;
  }

  return (
    <>
      <NodeTestingComponent
        node={node as import('reactflow').Node}
        isRunning={isTesting}
        output={testOutput}
        error={error}
        input={input}
        onTestNode={handleTestNode}
      />
      
      {isStorageTriggerWaiting && (
        <StorageTriggerWaitingDialog
          isOpen={isStorageTriggerWaiting}
          nodeId={currentStorageTriggerNodeId}
          onClose={() => setIsStorageTriggerWaiting(false)}
          onStorageData={handleStorageData}
          onTimeout={handleStorageTriggerTimeout}
          organizationId={organizationId}
          workspaceId={workspaceId}
          fileTypes={fileTypes}
          useSSE={true}
        />
      )}
    </>
  );
}
